import { Component, OnDestroy, OnInit, ViewChild, ElementRef, HostListener, ValueProvider } from '@angular/core';
import { SettingsService } from '../../../services/settings.service';
import { SynchronizationService } from '../../../services/synchronization.service';
import { CommunicationService } from '../../../services/communication.service';
import { ConfigLoaderService } from '../../../config/configloader.service';
import { MessageService } from '../../../services/message.service';
import { NotifierService } from 'angular-notifier';
import { AwallComponentEvent } from '../../../models/awallComponentEvent';
import { Subscription } from 'rxjs';
import { MockVideoHelper } from './vtr-widget-mock-video-helper';
import { WebRTCEvent } from '../../../models/syncEvents/webRTCEvents/webRTCEvent';
import { WebRTCEventGotUserMedia } from '../../../models/syncEvents/webRTCEvents/webRTCEventGotUserMedia';
import { SyncEventType } from '../../../models/enums/syncEventType';
import { ComponentEvent } from '../../../models/enums/componentEvent';
import { CopilotService } from '../../../services/copilot.service';

@Component({
  selector: 'app-vtr-widget',
  templateUrl: './vtr-widget.component.html',
  styleUrls: ['./vtr-widget.component.scss']
  })
export class VtrWidgetComponent implements OnInit, OnDestroy {
  public mockVideos = false; // for dev-purposes, use mockvideos to simulate multiple video-connections
  static widgetName = 'vtr';
  private subscriptions: Subscription[] = [];
  public connected = false;
  public isVolumeOff = false;
  public usercount = 0;
  @ViewChild('videocontainer') element: ElementRef;
  private currentProjectKey: string;
  private currentSprintId: number;
  // Collection of RTCPeerDConnections mapped by id
  private rtcPeerConnections = new Map<string, RTCPeerConnection>();
  // The HTML-Element showing the video of this client
  localVideoElement: HTMLMediaElement;
  // The id-Attribute of the localVideoElement
  localVideoElementId = 'localVideo';
  // The HTML-Elments showing the videos of connected remote clients
  public remoteVideoElements = new Map();
  private constraints = { audio: true, video: true };
  public backgroundVideoIsActive = false;
  public videoCanvasUpdaters = new Map<string, any>();
  // Interval in millisec between updates (re-draws) of the fullscreen-canvas
  // Lower numbers (more updates) increase the CPU-load dramatically
  readonly fullscreenUpdateInterval = 120;
  private rtcConfig: RTCConfiguration;
  public copilotState = false;
  private mediaStream: MediaStream = null;

  constructor(
    private settings: SettingsService,
    private configService: ConfigLoaderService,
    private sync: SynchronizationService,
    private messageService: MessageService,
    private communication: CommunicationService,
    private mockHelper: MockVideoHelper,
    private elementRef: ElementRef,
    private notifier: NotifierService,
    private copilotService: CopilotService,
  ) {
  }

  ngOnInit(): void {
    if (this.mockVideos) {
      this.mockHelper.init(this, this.elementRef);
      return;
    }

    this.rtcConfig = this.configService.settings.RTCConfiguration;
    this.setupWebRTCEventListener();
    this.initUserCountEventSubscription();
    this.initSettingsSubscription();
    this.currentProjectKey = this.settings.getProjectName();
    this.currentSprintId = this.settings.getSprintId(this.currentProjectKey);
    this.sync.joinPersistenceRoom(this.currentProjectKey);
  }

  ngOnDestroy(): void {
    this.disconnectFromVTR();
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
    this.getVideoContainer().innerHTML = '';
    this.getFullscreenVideoContainer().innerHTML = '';
  }

  toggleMockVideo(): void {
    // clean up mockHelper if we were in mock-mode
    if (this.mockVideos) {
      this.stopAllFullscreenUpdaters();
    }

    this.mockVideos = !this.mockVideos;
    this.ngOnDestroy();
    this.ngOnInit();
  }

  toggleVTRConnectionState(): void {
    if (!this.connected) {
      this.connectoToVTR();
    } else {
      this.disconnectFromVTR();
    }
  }

  toggleCopilotState(): void {
    this.copilotState = !this.copilotState;
    if (this.copilotState === false) {
      this.copilotService.disconnect();
    } else {
      if (this.mediaStream) {
        this.messageService.sendDebug('Enter copilot meeting');
        this.copilotService.connect(this.mediaStream, this.currentProjectKey, this.sync.getSocketId());
      }
    }
    this.messageService.sendInfo(this.copilotState ? 'Copilot activated' : 'Copilot deactivated');
  }

  /**
   * Subscribe to message-event that are used for the WebRTC-Connection
   */
  private setupWebRTCEventListener(): void {
    const webRTCSubscription: Subscription = this.sync.subscribeEvent<any>('webRTCEvent').subscribe(messageEvent => {
      this.handleWebRTCEvents(messageEvent);
    });
    this.subscriptions.push(webRTCSubscription);
  }

  /**
   * Subscribe the 'usercount'-event to get updated on changing numbers of connected vtr-users
   */
  private initUserCountEventSubscription(): void {
    const userCountSubscription: Subscription = this.sync.subscribeEvent<any>('usercount').subscribe(value => {
      this.usercount = value.usercount;
    });
    this.subscriptions.push(userCountSubscription);
  }

  /**
   * Subscribing to the settings-Observable to get notified when project/sprint changes.
   */
  private initSettingsSubscription(): void {
    const settingsSubscription: Subscription = this.settings.getUpdateObservable().subscribe(() => {
      const settingsProjectKey = this.settings.getProjectName();
      const settingsSprintId = this.settings.getSprintId(settingsProjectKey);
      const projectChanged = (this.currentProjectKey !== settingsProjectKey);
      const sprintChanged = (this.currentSprintId !== settingsSprintId);

      // Project and possibly sprint changed
      if (projectChanged) {
        this.onProjectChanged(settingsProjectKey, settingsSprintId);
      }
      // Only sprint changed
      else if (sprintChanged) {
        this.onSprintChanged(settingsSprintId);
      }
    });
    this.subscriptions.push(settingsSubscription);
  }

  /**
  * If the currently selected * project * changes the VTR is disconnected.
  */
  private onProjectChanged(newProjectKey: string, newSprintId: number): void {
    if (this.connected) {
      this.disconnectFromVTR();
    }

    this.currentProjectKey = newProjectKey;
    this.currentSprintId = newSprintId;
    this.sync.joinPersistenceRoom(this.currentProjectKey);
  }

  private onSprintChanged(newSprintId: number): void {
    this.currentSprintId = newSprintId;
    this.sync.joinPersistenceRoom(this.currentProjectKey);
    if (this.connected) {
      this.sync.joinRealtimeRoom(this.currentProjectKey);
    }
  }

  connectoToVTR(): void {
    // Connect to realtime room for synchronisation
    const projectKey = this.settings.getProjectName();
    this.sync.joinRealtimeRoom(projectKey);

    // Try to connect get a Turn-Server-Token and establish a video-connection
    this.configService.getTurnServerToken().subscribe(tokenData => {
      this.overwriteDefaultIceServerCredentials(tokenData);
      this.joinVideoRoom();
    });

    this.connected = true;
  }

  /**
   * Overwrite the default-credentials from config-file with the freshly aquired credentials from pywall (if valid).
   */
  private overwriteDefaultIceServerCredentials(tokenData: object): void {
    if (tokenData && tokenData.hasOwnProperty('username') && tokenData.hasOwnProperty('credential')) {
      this.rtcConfig.iceServers.forEach(serverEntry => {
        if (serverEntry.hasOwnProperty('username') && serverEntry.hasOwnProperty('credential')) {
          serverEntry['username'] = tokenData['username'];
          serverEntry['credential'] = tokenData['credential'];
        }
      });
    }
  }

  // This annotation makes sure we terminate gracefully on browser reload when no 'onDestroy' gets called
  @HostListener('window:beforeunload')
  disconnectFromVTR(): void {
    if (this.connected) {
      this.leaveVideoRoom();
    }
    this.sync.leaveRealtimeRoom();
    this.connected = false;
    this.copilotState = false;
    this.stopAllFullscreenUpdaters();
  }

  handleWebRTCEvents(event): void {
    const message = event.message;
    const type = event.messageType;
    const id = event.connectionId;

    console.log('message: ' + type + ' for ID: ' + id);
    switch (type) {
      case SyncEventType.WEBRTC_GOT_USER_MEDIA: {
        this.createRTCPeerConnection(id);
        this.createRTCConnectionOffer(id);
        break;
      }
      case 'video-offer': {
        this.createRTCPeerConnection(id);
        const connection = this.rtcPeerConnections.get(id);
        if (connection) {
          connection.setRemoteDescription(new RTCSessionDescription(message));
          this.createRTCConnectionAnswer(id);
        }
        break;
      }
      case 'video-answer': {
        const connection = this.rtcPeerConnections.get(id);
        if (connection) {
          connection.setRemoteDescription(new RTCSessionDescription(message));
        }
        break;
      }
      case 'new-ice-candidate': {
        const connection = this.rtcPeerConnections.get(id);
        if (connection) {
          // The candidate can be 'null'. This is used by WebRTC to signal the end of candidate-gathering and
          // is not a real error.
          const candidate = (message.candidate) ? new RTCIceCandidate(message.candidate) : null;
          connection.addIceCandidate(candidate).catch(function (error) {
            console.log('Received null-ice-candidate: ', message.candidate);
          });
        }
        break;
      }
      case 'hang-up': {
        this.disposeConnectionAndVideo(id);
        break;
      }
      default: {
        this.notifier.notify('error', 'Incoming VTR-Message has unrecognized type: ' + type);
        break;
      }
    }
  }

  private joinVideoRoom(): void {
    const localThis = this;
    // Check if mediaDevices is supported. Might not be supported in unsecure context.
    if (!navigator.mediaDevices) {
      localThis.notifier.notify('error', 'MediaDevices not supported.');
      return;
    }

    this.messageService.sendInfo('Joining videoroom with ID for this clients connection: ' + this.sync.getSocketId());
    const successfullyJoinedVideoRoom = this.sync.joinVideoRoom(this.currentProjectKey);

    if (!successfullyJoinedVideoRoom) {
      return;
    }

    this.localVideoElement = this.addNewVideoToVideoContainer(this.localVideoElementId);
    this.localVideoElement.muted = true;

    navigator.mediaDevices.getUserMedia(this.constraints).then(function (localStream: MediaStream) {
      localThis.mediaStream = localStream;
      localThis.localVideoElement.srcObject = localStream;
      localThis.sendWebRTCEvent2(new WebRTCEventGotUserMedia());

    }).catch(function (error) {
      localThis.notifier.notify('error', 'Video-Stream error: ' + error.message);
      localThis.messageService.sendError('Error Opening Video-Steam: ' + error);
      localThis.leaveVideoRoom();
    });
  }

  private leaveVideoRoom(): void {
    this.copilotService.disconnect();

    // / the second parameter is purely informational here, i.e. to check in syncserver is the message is delivered
    this.sendWebRTCEvent('hang-up', { info: 'client is disconnecting.' });
    this.disposeAllConnectionsAndVideos();
    this.sync.leaveVideoRoom();
  }


  /**
   * Sends a webRTCEvent to the pySyncServer which acts as a signaling server for WebRTC.
   * If a onlySendToConnectionId is set, the webRTCEvent will only be delivered to this client.
   * Else the webRTCEvent will be sent to all clients in the same video-room, defined by the current project and sprint
   */
  private sendWebRTCEvent(eventName: string, payload, onlySendToConnectionId?: string): void {
    const dataToSend = {
      'projectId': this.currentProjectKey,
      'sprintId': this.currentSprintId,
      'message': payload,
      'messageType': eventName,
      'connectionId': this.sync.getSocketId()
    };
    if (onlySendToConnectionId) {
      dataToSend['recipientConnectionId'] = onlySendToConnectionId;
    }
    this.sync.sendEvent('webRTCEvent', dataToSend);
  }

  // TODO WIP, setting up the method to accept webRTCEvent objects
  private sendWebRTCEvent2(event: WebRTCEvent, onlySendToConnectionId?: string): void {
    const dataToSend = {
      'projectId': this.currentProjectKey,
      'sprintId': this.currentSprintId,
      'message': event.getPayload(),
      'messageType': event.type,
      'connectionId': this.sync.getSocketId()
    };
    if (onlySendToConnectionId) {
      dataToSend['recipientConnectionId'] = onlySendToConnectionId;
    }
    this.sync.sendEvent('webRTCEvent', dataToSend);
  }

  private addNewVideoToVideoContainer(videoId: string): HTMLVideoElement {
    const localThis = this;
    const video = document.createElement('video');
    video.classList.add('videoelement');
    video.autoplay = true;
    video.controls = false;
    video.load();
    video.setAttribute('id', videoId);
    video.setAttribute('data-videoid', videoId);
    video.onclick = function (event) {
      // if this is the second click of a double-click we dont react
      if (event.detail < 2) {
        localThis.onVideoElementClicked(event);
      }
    };
    video.ondblclick = function (event) {
      localThis.onVideoThumbnailDoubleclicked(event);
    };

    this.getVideoContainer().appendChild(video);
    return video;
  }

  /**
   * If the video-element of a remote video is clicked we move this video to the fullscreen-container and show
   * a button in the VTR-Widget that can minimize the fullscreen again.
   * @param event mouse-click event registerd on a video-element
   */
  private onVideoElementClicked(event): void {
    event.stopImmediatePropagation();
    const clickedVideoElement: HTMLVideoElement = <HTMLVideoElement>event.target;
    const videoIsFullscreen = this.videoIsShownInFullscreen(clickedVideoElement);

    if (!videoIsFullscreen) {
      this.addVideoToFullscreen(clickedVideoElement);
    }
    else {
      this.removeVideoFromFullscreen(clickedVideoElement);
    }
    this.updateGridSize();
  }

  // On doublecklick open/close all remote videos in fullscreen
  private onVideoThumbnailDoubleclicked(event): void {
    const allVideos = Array.from(this.getVideoContainer().children);
    // if doubleclick on a video in fullscreen, we remove all, else we show all videos fullscreen
    const showAll = this.videoIsShownInFullscreen(<HTMLVideoElement>event.target);

    allVideos.forEach(video => {
      const videoElement: HTMLVideoElement = <HTMLVideoElement>video;
      const videoIsFullscreen = this.videoIsShownInFullscreen(videoElement);

      if (showAll && !videoIsFullscreen) {
        this.addVideoToFullscreen(videoElement);
      }
      if (!showAll && videoIsFullscreen) {
        this.removeVideoFromFullscreen(videoElement);
      }
    });
    this.updateGridSize();
  }

  private addVideoToFullscreen(videoElement: HTMLVideoElement): void {
    // Change border color of videoElement that is in fullscreenmode
    videoElement.classList.add('in-fullscreen');

    // Create CanvasElement on which fullscreen is displayed
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    canvas.id = videoElement.id;
    canvas.setAttribute('data-videoid', videoElement.id);

    const container = this.getFullscreenVideoContainer();
    container.appendChild(canvas);
    this.updateBackgroundVideoState();
    this.startFullscreenUpdate(canvas, videoElement);
  }

  private removeVideoFromFullscreen(videoElement: HTMLVideoElement): void {
    // Remove highlight-border from videoelement
    videoElement.classList.remove('in-fullscreen');
    // stop and destroy the updating function that draws on the fullscreen-canvas
    this.stopFullscreenUpdate(videoElement);
    // remove all child-elements from the fullscreencontainer
    const fullscreenCanvas = this.getFullscreenCanvasOfVideo(videoElement);
    fullscreenCanvas.remove();

    this.updateBackgroundVideoState();
  }

  public updateBackgroundVideoState() {
    const currentState = this.getBackgroundVideoState();

    if (this.backgroundVideoIsActive !== currentState) {
      this.backgroundVideoIsActive = currentState;
      this.communication.sendToAll(
        new AwallComponentEvent(ComponentEvent.WIDGET_VTR_VIDEO_ACTIVE, this.backgroundVideoIsActive)
      );
      this.styleFullscreenVideoContainer();
    }
  }

  /**
   * Returns true if a video is shown in the background (in fullscreenVideoContainer)
   */
  public getBackgroundVideoState(): boolean {
    const fullscreenModeActive = this.getFullscreenVideoContainer().hasChildNodes();
    return fullscreenModeActive;
  }

  // Finds the Update-function for this video/canvas stops it, removes it from the map and destroys it
  public stopFullscreenUpdate(videoElement: HTMLVideoElement): void {
    if (this.videoCanvasUpdaters.has(videoElement.id)) {
      let updater = this.videoCanvasUpdaters.get(videoElement.id);
      this.videoCanvasUpdaters.delete(videoElement.id);
      clearInterval(updater);
      updater = null;
    }
  }

  public stopAllFullscreenUpdaters(): void {
    this.videoCanvasUpdaters.forEach(updater => {
      clearInterval(updater);
      updater = null;
    });
    this.videoCanvasUpdaters = new Map<string, any>();
  }

  // returns the canvas-element that is the fullscreen-representation of the videoelement, or null if
  // this videoelement is not in fullscreen and therefor has no corresponding canvas
  public getFullscreenCanvasOfVideo(videoElement: HTMLVideoElement): HTMLElement {
    const fullscreenContainer: HTMLElement = this.getFullscreenVideoContainer();
    const canvas: HTMLElement = fullscreenContainer.querySelector('canvas[data-videoid="' + videoElement.id + '"]');
    return canvas;
  }

  public videoIsShownInFullscreen(videoElement: HTMLVideoElement): boolean {
    const videoIsFullscreen = (this.getFullscreenCanvasOfVideo(videoElement) != null);
    return videoIsFullscreen;
  }

  public startFullscreenUpdate(canvas: HTMLCanvasElement, videoElement: HTMLVideoElement): void {
    // if there already is an updater we remove it before adding a new one
    this.stopFullscreenUpdate(videoElement);

    const updater = setInterval(function () {
      canvas.getContext('2d').drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    }, this.fullscreenUpdateInterval);

    this.videoCanvasUpdaters.set(videoElement.id, updater);
  }

  private removeVideoFromVideoContainer(videoId: string): void {
    const videoContainer = this.getVideoContainer();
    const videoElement = videoContainer.querySelector('video[data-videoid="' + videoId + '"]');
    if (videoElement) {
      videoContainer.removeChild(videoElement);
    }
  }

  /**
   * If the localStream is established, this creates a new RTCPeerConnection that is added
   * to the rtcPeerConnections-Collection and connects the localstream to this connection.
   *
   * Note that this can fail in which case no connection is added to the rtcPeerConnections-Collection
   *
   * @param connectionId connectionId of the peer for which a connection should be created
   */
  private createRTCPeerConnection(connectionId: string): void {
    const localStream: MediaStream = <MediaStream>this.localVideoElement.srcObject;
    const localStreamHasTracks = (localStream && localStream.getTracks() && localStream.getTracks().length > 0);
    const connection: RTCPeerConnection = this.createPeerConnection(connectionId);

    if (localStreamHasTracks && connection) {
      localStream.getTracks().forEach(track => {
        connection.addTrack(track, localStream);
      });
      if (this.rtcPeerConnections.has(connectionId)) {
        console.error('Trying to add a connection to map for an already existing one');
      } else {
        this.rtcPeerConnections.set(connectionId, connection);
      }
    }
    else {
      console.log('VTR Could not add tracks for: ' + connectionId);
      this.destroyPeerConnection(connection);
    }
  }

  private createPeerConnection(connectionId: string): RTCPeerConnection {
    const localThis = this;
    let connection: RTCPeerConnection;
    try {
      connection = new RTCPeerConnection(this.configService.settings.RTCConfiguration);

      // Handle 'new-ice-candidate'-Event by sending a corresponding message to the signalling-server (pySyncServer)
      connection.onicecandidate = function (event) {
        const candidate = (event) ? event.candidate : null;
        localThis.sendWebRTCEvent('new-ice-candidate', { type: 'candidate', candidate }, connectionId);
      };
      // Add a new videoElement for each videotrack and connect the stream to this videoElement
      // We dont react to any other kinds of tracks
      connection.ontrack = function (event: RTCTrackEvent) {
        if (event.track.kind === 'video') {
          localThis.remoteVideoElements.set(connectionId, localThis.addNewVideoToVideoContainer(connectionId));
          localThis.remoteVideoElements.get(connectionId).srcObject = event.streams[0];
        }
      };
      connection.removeTrack = this.handleRemoteStreamRemoved;
      // TODO check if there are other eventlisteners we should implement
      return connection;
    } catch (e) {
      this.destroyPeerConnection(connection);
      localThis.notifier.notify('error', 'RTCPeerConnection error: ' + e.message);
      console.log('Cannot create RTCPeerConnection object. ', e.message);
      return null;
    }
  }

  private handleCreateOfferError(event) {
    this.messageService.sendError('handleCreateOfferError');
    console.log('RTCPeerConnection offer error: ', event.message);
    this.notifier.notify('error', 'RTCPeerConnection offer error: ' + event.message);
  }

  private handleCreateAnswerError(event) {
    this.messageService.sendError('handleCreateAnswerError');
    console.log('RTCPeerConnection answer error: ', event.message);
    this.notifier.notify('error', 'RTCPeerConnection answer error: ' + event.message);
  }

  private createRTCConnectionOffer(connectionId: string) {
    if (!this.rtcPeerConnections.has(connectionId)) {
      return;
    }
    const localThis = this;
    const connection: RTCPeerConnection = this.rtcPeerConnections.get(connectionId);

    connection.createOffer().then(function (offer) {
      connection.setLocalDescription(offer);
      localThis.sendWebRTCEvent('video-offer', offer, connectionId);
    }).catch(function (reason) {
      localThis.handleCreateOfferError(reason);
    });
  }

  private createRTCConnectionAnswer(connectionId: string) {
    if (!this.rtcPeerConnections.has(connectionId)) {
      return;
    }
    const localThis = this;
    const connection: RTCPeerConnection = this.rtcPeerConnections.get(connectionId);

    connection.createAnswer().then(function (answer) {
      connection.setLocalDescription(answer);
      localThis.sendWebRTCEvent('video-answer', answer, connectionId);
    }).catch(function (reason) {
      localThis.handleCreateAnswerError(reason);
    });
  }

  // TODO check if we need to do something here
  private handleRemoteStreamRemoved(event): void {
    this.messageService.sendInfo('Console info');
    console.log('>> REMOTE stream removed. Event: ', event);
  }
  /**
   * Dispose of all local and remote RTCPeerConnections and corresponding videos
   */
  private disposeAllConnectionsAndVideos(): void {
    // Dispose local Video
    this.disposeConnectionAndVideo(this.localVideoElementId);
    // Dispose all remote connectiosn and videos
    this.remoteVideoElements.forEach((remoteVideoElement, connectionId) => {
      this.disposeConnectionAndVideo(connectionId);
    });
    this.remoteVideoElements.clear();
  }

  /**
 * Disposes of the RTCPeerConnection and videoElement that refer to connectionId.
 * @param connectionId id used for the videoElement and RTCPeerConnection. If this id refers to the localVideoElement there will be no
 * corresponding RTCPeerConnection.
 */
  private disposeConnectionAndVideo(connectionId: string): void {
    this.destroyRegistredPeerConnection(connectionId);

    // Find the correct videoElement stop it, remove references and and remove it from DOM and Collection
    let videoElement: HTMLMediaElement = null;
    if (connectionId === this.localVideoElementId) {
      videoElement = this.localVideoElement;
    } else {
      videoElement = this.remoteVideoElements.get(connectionId);
    }
    if (videoElement != null) {
      videoElement.pause();

      const stream: MediaStream = <MediaStream>videoElement.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      videoElement.srcObject = null;

      this.removeVideoFromVideoContainer(connectionId);

      if (this.remoteVideoElements.has(connectionId)) {
        this.remoteVideoElements.delete(connectionId);
      }
      if (this.videoIsShownInFullscreen(<HTMLVideoElement>videoElement)) {
        this.removeVideoFromFullscreen(<HTMLVideoElement>videoElement);
      }
    }
  }

  /**
   * Close the RTCPeerConnection with id <connectionId> and remove all its references
   */
  private destroyRegistredPeerConnection(connectionId: string): void {
    const connection: RTCPeerConnection = this.rtcPeerConnections.get(connectionId);
    this.rtcPeerConnections.delete(connectionId);
    this.destroyPeerConnection(connection);
  }

  private destroyPeerConnection(connection: RTCPeerConnection): void {
    if (connection) {
      connection.close();
      connection = null;
    }
  }

  getVideoContainer(): HTMLElement {
    return document.getElementById('vtr-videocontainer');
  }

  getFullscreenVideoContainer(): HTMLElement {
    return document.getElementById('fullscreen_video_container');
  }

  /**
   * We add/remove a CSS class here to make the issuecards in the Container semitransparent
   * while a background-video is active
   */
  styleFullscreenVideoContainer(): void {
    const container: HTMLElement = this.getFullscreenVideoContainer();
    const cssClass = 'transparent-background';
    if (this.backgroundVideoIsActive) {
      container.classList.add(cssClass);
    }
    else {
      container.classList.remove(cssClass);
    }
  }

  /**
   * Toggles mute on all remote videos (local video is muted by default)
   */
  toggleAudio(): void {
    this.isVolumeOff = !this.isVolumeOff;
    this.remoteVideoElements.forEach(element => {
      (<HTMLVideoElement>element).muted = this.isVolumeOff;
    });
  }

  // Recalculate the optimal gridsize so all videos of fullscreen-videocontainer fit,
  // and update CSS accordingly
  public updateGridSize(): void {
    const videoCount = this.getFullscreenVideoContainer().childElementCount;
    const gridSize = this.calculateGridSize(videoCount);
    this.updateGridCSS(gridSize);
  }

  /**
     * This calculates what size a quadratic grid has to be in order to fit videoCount videos.
     * ie. videoCount = 5 -> result = 3. 3x3 is the smallest quadratic grid that can fit 5 videos.
     */
  private calculateGridSize(videoCount: number): number {
    let gridSize = 1;
    while ((gridSize * gridSize) < videoCount) {
      gridSize += 1;
    }
    return gridSize;
  }

  private updateGridCSS(gridSize: number): void {
    const target: HTMLElement = this.getFullscreenVideoContainer();
    const columnValue = Math.trunc(100 / gridSize) + '% ';

    target.style.gridTemplateRows = 'auto '.repeat(gridSize);
    target.style.gridTemplateColumns = columnValue.repeat(gridSize);
  }

  /**
   * Debug-Method to show the created connections and videoelements
   */
  private debugShowPeerMaps(): void {
    console.log('PEER CONNECTIONS');
    this.rtcPeerConnections.forEach((connection, connectionId) => {
      console.log('ID: ' + connectionId + ', Connection: ', connection);
    });

    console.log('PEER VIDEOS');
    this.remoteVideoElements.forEach((element, connectionId) => {
      console.log('ID: ' + connectionId + ', RemoteVideoElement: ', element);
    });
  }
}
